import { useRef, useState } from 'react';
import {
  Box,
  Button,
  buttonClasses,
  ButtonGroup,
  ClickAwayListener,
  MenuItem,
  MenuList,
  Paper,
  paperClasses,
  Popper,
  styled,
  Typography,
  typographyClasses,
} from '@mui/material';
import { IconCaretDown } from 'assets/img';
import { SplitButtonBaseOption, SplitButtonProps } from './split-button.types';

export const LwSplitButton = <T extends SplitButtonBaseOption>({
  primaryLabel,
  primaryStartIcon,
  primaryEndIcon,
  dropdownIcon,
  onClick,
  disabledPrimary,
  disabledDropdown,
  options,
  renderOption,
  dataTestId,
}: SplitButtonProps<T>) => {
  const [open, setOpen] = useState<boolean>(false);
  const anchorRef = useRef<HTMLDivElement>(null);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event: Event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpen(false);
  };

  const handleOptionClick = (callback: () => void) => () => {
    callback();
    setOpen(false);
  };

  return (
    <>
      <StyledButtonGroup variant="contained" ref={anchorRef}>
        <StyledPrimaryButton
          onClick={onClick}
          disabled={disabledPrimary}
          startIcon={primaryStartIcon}
          endIcon={primaryEndIcon}
          data-testid={`${dataTestId}-primary-button`}
        >
          {primaryLabel}
        </StyledPrimaryButton>
        <StyledDropdownButton
          disableRipple
          size="small"
          aria-controls={open ? 'split-button-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          aria-haspopup="menu"
          onClick={handleToggle}
          data-testid={`${dataTestId}-dropdown-button`}
          disabled={disabledDropdown}
        >
          {dropdownIcon ? dropdownIcon : <StyledIcon width={24} height={24} as={IconCaretDown} />}
        </StyledDropdownButton>
      </StyledButtonGroup>
      <StyledPopper open={open} anchorEl={anchorRef.current} disablePortal>
        <Paper>
          <ClickAwayListener onClickAway={handleClose}>
            <StyledMenuList id="split-button-menu" autoFocusItem>
              {options?.map((option, index) =>
                renderOption ? (
                  renderOption(option, index)
                ) : (
                  <StyledMenuItem
                    key={index}
                    onClick={handleOptionClick(option.onClick)}
                    disabled={option.disabled}
                    isLastItem={index === options.length - 1}
                  >
                    <Box>
                      <Typography>{option.label}</Typography>
                      {option.subLabel && <StyledSubLabel>{option.subLabel}</StyledSubLabel>}
                    </Box>
                  </StyledMenuItem>
                )
              )}
            </StyledMenuList>
          </ClickAwayListener>
        </Paper>
      </StyledPopper>
    </>
  );
};

const StyledPrimaryButton = styled(Button)(({ theme }) => ({
  [`&.${buttonClasses.contained}`]: {
    backgroundColor: theme.palette.lwPrimary[100],
    borderRadius: `${theme.spacing(7)} 0 0 ${theme.spacing(7)}`,
    color: theme.palette.lwWhite[100],
    fontSize: '16px',
    fontWeight: 700,
    fontVariationSettings: '"wght" 700',
    gap: theme.spacing(2),
    lineHeight: theme.spacing(5),
    height: '56px',
    padding: theme.spacing(4, 6),
    textTransform: 'none',
    borderRight: `1px solid ${theme.palette.lwWhite[100]}`,

    '&:hover': {
      backgroundColor: theme.palette.lwPrimary[100],
      boxShadow: `0px 12px 32px ${theme.palette.lwSecondary[40]}`,
      color: theme.palette.lwWhite[100],
    },

    '&:focus': {
      boxShadow: `0px 0px 0px 4px ${theme.palette.lwSecondary[10]}`,
    },

    '&:active': {
      backgroundColor: theme.palette.lwSecondary[100],
      color: theme.palette.lwWhite[80],
    },

    '&.Mui-disabled': {
      backgroundColor: theme.palette.lwPrimary[20],
      color: theme.palette.lwWhite[80],
      borderRight: `1px solid ${theme.palette.lwWhite[100]}`,
    },
  },
}));

const StyledDropdownButton = styled(Button)(({ theme }) => ({
  [`&.${buttonClasses.root}`]: {
    alignItems: 'center',
    boxShadow: 'none',
    display: 'flex',
    fontFamily: 'Urbanist',
    fontWeight: 'bold',
    justifyContent: 'center',
    textTransform: 'none',

    '&.Mui-disabled': {
      cursor: 'not-allowed',
      pointerEvents: 'none',
    },
  },
  [`&.${buttonClasses.contained}`]: {
    backgroundColor: theme.palette.lwPrimary[100],
    borderRadius: `0 ${theme.spacing(7)} ${theme.spacing(7)} 0 `,
    color: theme.palette.lwWhite[100],
    fontSize: '16px',
    fontWeight: 700,
    fontVariationSettings: '"wght" 700',
    gap: theme.spacing(2),
    lineHeight: theme.spacing(5),
    height: '56px',
    padding: theme.spacing(4, 6),

    '&:hover': {
      backgroundColor: theme.palette.lwPrimary[100],
      boxShadow: `0px 12px 32px ${theme.palette.lwSecondary[40]}`,
      color: theme.palette.lwWhite[100],
    },

    '&:focus': {
      boxShadow: `0px 0px 0px 4px ${theme.palette.lwSecondary[10]}`,
    },

    '&:active': {
      backgroundColor: theme.palette.lwSecondary[100],
      color: theme.palette.lwWhite[80],
    },

    '&.Mui-disabled': {
      backgroundColor: theme.palette.lwPrimary[20],
      color: theme.palette.lwWhite[80],
    },
  },
}));

const StyledButtonGroup = styled(ButtonGroup)(({ theme }) => ({
  boxShadow: 'none',
  backgroundColor: theme.palette.lwPrimary[100],
  borderRadius: theme.spacing(7),
  border: 'none',
}));

const StyledIcon = styled('svg')(({ theme }) => ({
  '& path': {
    stroke: theme.palette.lwWhite[100],
  },
}));

const StyledPopper = styled(Popper)(({ theme }) => ({
  [`& .${paperClasses.root}`]: {
    marginTop: theme.spacing(2),
    zIndex: 1,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(2, 0),
    width: '200px',
    boxShadow: `0px 0px 0px 2px ${theme.palette.lwSecondary[10]}`,
  },
}));

const StyledMenuList = styled(MenuList)(() => ({
  padding: 0,
}));

const StyledMenuItem = styled(MenuItem, { shouldForwardProp: (prop) => prop !== 'isLastItem' })<{
  isLastItem?: boolean;
}>(({ theme, isLastItem }) => ({
  borderBottom: isLastItem ? 'none' : `1px solid ${theme.palette.lwBlack[10]}`,
  padding: theme.spacing(3),
}));

const StyledSubLabel = styled(Typography)(({ theme }) => ({
  [`&.${typographyClasses.root}`]: {
    color: theme.palette.lwBlack[60],
    fontSize: '12px',
    marginTop: theme.spacing(0.5),
  },
}));
